import React, { createContext, useReducer } from "react";
import { sanitizeTicket } from "../../helpers/sanitizeContact"; 

const getTicketSignature = ticket => ({
    id: ticket.id,
    status: ticket.status,
    updatedAt: ticket.updatedAt,
    lastMessage: ticket.lastMessage,
    unreadMessages: ticket.unreadMessages,
    isFavorite: ticket.isFavorite,
    title: ticket.title,
    userId: ticket.userId,
    queueId: ticket.queueId,
    whatsappId: ticket.whatsappId,
    queueName: ticket.queue?.name,
    queueColor: ticket.queue?.color,
    whatsappName: ticket.whatsapp?.name,
    contactName: ticket.contact?.name,
    contactProfilePicUrl: ticket.contact?.profilePicUrl,
});

const isSameVisibleTicket = (current, next) => {
    if (!current || !next) return false;

    const currentSignature = getTicketSignature(current);
    const nextSignature = getTicketSignature(next);

    return Object.keys(currentSignature).every(key => currentSignature[key] === nextSignature[key]);
};

export const reducer = (state, action) => {
    const { user } = action.meta || {};
  
    let newState;

    switch (action.type) {
        case "LOAD_TICKETS": {
            if (!user) {
                newState = state;
                break;
            }

            const incoming = action.payload || [];
            const sanitizedIncoming = incoming.map(ticket => sanitizeTicket(ticket, user));

            const map = new Map();
            state.forEach((ticket) => map.set(ticket.id, ticket));
            sanitizedIncoming.forEach((ticket) => {
                const current = map.get(ticket.id);
                map.set(ticket.id, isSameVisibleTicket(current, ticket) ? current : ticket);
            });

            newState = [...map.values()];
            break;
        }

        case "UPDATE_TICKET_FAVORITE": {
            if (!user) {
                newState = state;
                break;
            }
            const ticket = sanitizeTicket(action.payload, user);
            const showFavoriteTickets = action.meta?.showFavoriteTickets;

            if (showFavoriteTickets && !ticket.isFavorite) {
                newState = state.filter((t) => t.id !== ticket.id);
                break;
            }

            newState = state.map((t) => {
                if (t.id === ticket.id) {
                    return ticket;
                }
                return t;
            });
            break;
        }
        
        case "UPDATE_TICKET": {
    if (!user) {
        newState = state;
        break;
    }

    const ticket = sanitizeTicket(action.payload, user);
    const listStatus = action.meta?.listStatus;
    const showFavoriteTickets = action.meta?.showFavoriteTickets;
    const selectedTicketId = action.meta?.selectedTicketId;

    const oldTicket = state.find(t => t.id === ticket.id);

    // Caso: solo se modificó el title
    if (oldTicket && oldTicket.title !== ticket.title) {
        newState = state.map(t => {
            if (t.id !== ticket.id) return t;
            return {
                ...t,
                title: ticket.title,
                unreadMessages: t.id === selectedTicketId ? 0 : t.unreadMessages
            };
        });
        break; // Salimos sin mover el ticket
    }

    // Determinar si el ticket pertenece a esta lista según filtros
    const belongsToThisList =
        (!listStatus || ticket.status === listStatus) &&
        (!showFavoriteTickets || ticket.isFavorite);

    if (belongsToThisList) {
        // Para otros cambios, reemplazamos el ticket pero lo ponemos al inicio
        const filteredState = state.filter(t => t.id !== ticket.id);
        const finalTicket = {
            ...ticket,
            unreadMessages: ticket.id === selectedTicketId ? 0 : ticket.unreadMessages
        };
        newState = [finalTicket, ...filteredState];
    } else {
        // Si no pertenece a esta lista, lo eliminamos
        newState = state.filter(t => t.id !== ticket.id);
    }

    break;
}

  
        case "UPDATE_TICKET_UNREAD_MESSAGES": {
            const { ticketId, unreadMessages = 0 } = action.payload;
            const selectedTicketId = action.meta?.selectedTicketId;

            newState = state.map((ticket) => {
                if (ticket.id === ticketId) {
                    return {
                        ...ticket,
                        unreadMessages: ticket.id === selectedTicketId ? 0 : unreadMessages,
                    };
                }
                return ticket;
            });
            break;
        }
        
        case "UPDATE_TICKET_ON_MESSAGE": {
            if (!user) {
                newState = state;
                break;
            }

            const ticket = sanitizeTicket(action.payload, user);
            const selectedTicketId = action.meta?.selectedTicketId;

            const finalTicket = {
                ...ticket,
                unreadMessages:
                    ticket.id === selectedTicketId ? 0 : ticket.unreadMessages,
            };

            const currentIndex = state.findIndex((t) => t.id === ticket.id);
            const currentTicket = currentIndex >= 0 ? state[currentIndex] : null;
            const ticketToStore = isSameVisibleTicket(currentTicket, finalTicket)
                ? currentTicket
                : finalTicket;

            if (currentIndex >= 0) {
                if (currentIndex === 0 && ticketToStore === currentTicket) {
                    newState = state;
                    break;
                }
                const filteredState = state.filter((t) => t.id !== ticket.id);
                newState = [ticketToStore, ...filteredState];
            } else {
                newState = [finalTicket, ...state];
            }
            break;
        }
        
        case "DELETE_TICKET":
            newState = state.filter((t) => t.id !== action.payload);
            break;
        case "RESET":
            newState = [];
            break;
        case "RESET_UNREAD_MESSAGES": {
            const { ticketId } = action.payload;
            newState = state.map((ticket) => {
                if (ticket.id === ticketId) {
                    return { ...ticket, unreadMessages: 0 };
                }
                return ticket;
            });
            break;
        }
        default:
            newState = state;
            break;
    }
    return newState;
};

export const TicketsContext = createContext();

export const TicketsProvider = ({ children }) => {
    const [ticketsList, dispatch] = useReducer(reducer, []); 

    return (
        <TicketsContext.Provider value={{ ticketsList, dispatch }}>
            {children}
        </TicketsContext.Provider>
    );
};
